/**
 * Pinia store for managing locale settings in the application.
 * This store determines the current locale based on URL parameters, browser settings,
 * and our supported locales. It also provides utilities for handling and validating locales.
 *
 * @module LocaleStore
 */

import { defineStore } from 'pinia'
import { z } from 'zod'
import { useSearchParams } from '../composables/data-fetching/useSearchParams'

export const localeSchema = z
  .string()
  .regex(/^[A-Za-z]{2}(-([A-Za-z]{2})){0,1}$/)
  .transform((value) => ({
    language: value.split('-')[0].toLowerCase(),
    country: value.includes('-') ? value.split('-')[1].toUpperCase() : '',
  }))

export const supportedLocaleSchema = z
  .string()
  .regex(/^[a-z]{2}$/, 'Invalid language code')
export type SupportedLocales = z.infer<typeof supportedLocaleSchema>

export const supportedCountrySchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Invalid country code')
export type SupportedCountries = z.infer<typeof supportedCountrySchema>

const defaultLanguageCountryPairs: Record<
  SupportedLocales,
  SupportedCountries
> = {
  en: 'US',
  es: 'MX',
  fr: 'FR',
  pt: 'BR',
  de: 'DE',
  it: 'IT',
  pl: 'PL',
  tr: 'TR',
  ca: 'ES',
  ko: 'KR',
  uk: 'UA',
  da: 'DK',
  sv: 'SE',
  no: 'NO',
  hi: 'IN',
  ja: 'JP',
  tl: 'PH',
  th: 'TH',
  id: 'ID',
  vi: 'VN',
  ur: 'PK',
  cs: 'CZ',
  he: 'IL',
  sw: 'KE',
  bn: 'BD',
  ro: 'RO',
  hu: 'HU',
  el: 'GR',
  nl: 'NL',
  ar: 'AE',
  ta: 'SG',
}

/**
 * @returns {Object} - The reactive `locale` property.
 *
 * @example
 * const localeStore = useLocale()
 * localeStore.locale // 'EN' | 'FR' | 'DE' (static)
 * const { locale } = storeToRefs(localeStore) // from pinia
 * locale.value // 'EN' | 'FR' | 'DE' (reactive)
 */
export const useLocale = defineStore('locale', () => {
  const browserLocale = ref<SupportedLocales | undefined>(undefined)
  const browserCountry = ref<SupportedCountries | undefined>(undefined)
  const { selectedLang: localeParam } = useSearchParams()

  /**
   * Computed property that determines the current locale.
   * The locale is resolved in the following order:
   * 1. The first supported language from the `lang` URL parameter.
   * 2. The browser's locale.
   * 3. Defaults to 'EN' if no valid locale is found.
   */
  const locale = computed<SupportedLocales>(() => {
    if (Array.isArray(localeParam.value)) {
      return (
        firstSupportedLang(localeParam.value) ?? browserLocale.value ?? 'en'
      )
    }
    return supportedLang(localeParam.value ?? '') ?? browserLocale.value ?? 'en'
  })

  const country = computed<string | undefined>(() => {
    // If a lang param is set, we don't set a country to allow for mixed countries
    if (localeParam.value) {
      return undefined
    }

    return browserCountry.value ?? 'US'
  })

  // Utility functions

  const countryCode = (locale?: string): string => {
    if (!locale) return ''
    const parsed = localeSchema.safeParse(locale)
    if (!parsed.success) return ''
    return parsed.data.country
  }

  const languageCode = (locale?: string): string => {
    if (!locale) return ''
    const parsed = localeSchema.safeParse(locale)
    if (!parsed.success) return ''
    return parsed.data.language
  }

  const supportedLang = (lang: string): SupportedLocales | undefined => {
    const code = languageCode(lang)
    const parsedLang = supportedLocaleSchema.safeParse(code)
    return parsedLang.success ? parsedLang.data : undefined
  }

  const firstSupportedLang = (langs: string[] | readonly string[]) => {
    for (const lang of langs) {
      if (supportedLang(lang)) return supportedLang(lang)
    }
    return undefined
  }

  /* Returns the first supported country from a list of languages.
   * This function checks both country codes and language codes to determine
   * the appropriate country. If a valid country code is found, it is returned.
   * If only a valid language code is found, the default country for that language is returned.
   */
  const firstCountry = (langs: string[] | readonly string[]) => {
    for (const lang of langs) {
      const country = countryCode(lang)

      if (country) {
        const parsedCountry = supportedCountrySchema.safeParse(country)
        if (parsedCountry.success) {
          return parsedCountry.data
        }
        continue
      }

      const supportedLanguage = supportedLang(lang)
      if (supportedLanguage) {
        return getDefaultCountry(undefined, supportedLanguage)
      }
    }
    return undefined
  }

  // Returns the default country for a given language.
  const getDefaultCountry = (
    country: SupportedCountries | undefined,
    supportedBrowserLocale: SupportedLocales | undefined
  ): SupportedCountries => {
    if (!country) {
      return (
        defaultLanguageCountryPairs[supportedBrowserLocale ?? 'en'] ??
        defaultLanguageCountryPairs['en']
      )
    }

    return country
  }

  /**
   * Watches for changes in the `lang` URL parameter and updates it
   * to the first supported locale if necessary.
   */
  watch(
    () => localeParam.value,
    (newLang) => {
      if (!newLang) {
        localeParam.value = undefined
        return
      }
      const supportedParamLocale = Array.isArray(newLang)
        ? firstSupportedLang(newLang)
        : supportedLang(newLang)
      localeParam.value = supportedParamLocale
    },
    { immediate: true }
  )

  /**
   * Tracks the browser's language settings and updates the `browserLocale` accordingly.
   * This is initialized on store creation and updated whenever the browser's language changes.
   */
  const supportedBrowserLocale = firstSupportedLang(navigator.languages)
  browserLocale.value = supportedBrowserLocale

  const supportedBrowserCountry = firstCountry(navigator.languages)

  browserCountry.value = getDefaultCountry(
    supportedBrowserCountry,
    supportedBrowserLocale
  )

  window.addEventListener('languagechange', () => {
    const supportedBrowserLocale = firstSupportedLang(navigator.languages)
    const supportedBrowserCountry = firstCountry(navigator.languages)

    browserLocale.value = supportedBrowserLocale
    browserCountry.value = supportedBrowserCountry
  })

  return { locale, country }
})
